import type { NodeDefinition } from "@/generated";
import type { NodeV2Data } from "@/types/spotflow-v2";
import type { DynamicNodeConfig } from "@/store/editor-v2";
import { getNodeIconBgHex } from "@/components/SpotflowV2/nodeCategories";
import { isVariableTypedConnector } from "./mentionOptions";
import {
  normalizeVariableRefId,
  parseVarRefId,
  WHOLE_VAR_REF_PATTERN,
} from "./variableRefs";
import { parseFlowInputType } from "@/components/SpotflowV2/flowTypes";
import {
  declaredPathVar,
  formatPathVarLabel,
  pathKeysFor,
  splitPathOutputKey,
} from "./pathValues";
import { nodeIconName } from "@/components/SpotflowV2/subflowIcon";

export interface MentionSource {
  sourceNodeId: string;
  sourceName: string;
  sourceOutput: string;
  sourceImage: string | undefined;
  sourceIconName: string | undefined;
  sourceColor: string | undefined;
  isSecret: boolean;
  sourceIsList: boolean;
  sourceDataType: string | undefined;
}

interface RegistryOutput {
  name: string;
  label?: string;
  type?: string;
  data_type?: string;
  is_list?: boolean;
  definition?: { data_type?: string; is_list?: boolean };
}

export interface SecretLookupEntry {
  scope: string;
  name: string;
  dataType?: string;
}

function resolveSecretSource(
  qualifiedId: string,
  secretsByQualifiedId: Map<string, SecretLookupEntry> | undefined
): MentionSource {
  const secret = secretsByQualifiedId?.get(qualifiedId);
  return {
    sourceNodeId: "__secrets__",
    sourceName: secret
      ? secret.scope === "flow"
        ? "Flow variable"
        : "Workspace variable"
      : "Variable",
    sourceOutput: secret?.name ?? qualifiedId,
    sourceImage: undefined,
    sourceIconName: undefined,
    sourceColor: "#e0e7ff",
    isSecret: true,
    sourceIsList: false,
    sourceDataType: secret?.dataType ?? "str",
  };
}

/**
 * Resolve a single binding target — `sourceNodeId` + `outputName` decoded from
 * a binding value like "uuid.output" — to displayable chip metadata. The label
 * applies the same precedence as the sidebar editor: per-node output label
 * override → InputNode's config label → registry output label → raw output name.
 */
export function resolveBindingTarget(
  sourceNodeId: string,
  outputName: string,
  nodeDataMap: Map<string, NodeV2Data>,
  nodeRegistry: NodeDefinition[],
  secretsByQualifiedId?: Map<string, SecretLookupEntry>,
  dynamicNodeConfigs?: Map<string, DynamicNodeConfig>
): MentionSource {
  if (sourceNodeId === "__secrets__") {
    return resolveSecretSource(outputName, secretsByQualifiedId);
  }
  const sourceData = nodeDataMap.get(sourceNodeId);
  const sourceName = sourceData?.name ?? sourceNodeId;
  const sourceImage = sourceData?.image;
  const sourceIconName = nodeIconName(
    sourceData?.nodeType,
    sourceData?.nodeConfig
  );
  const sourceReg = nodeRegistry.find((r) => r.type === sourceData?.nodeType);
  const sourceColor = sourceData
    ? getNodeIconBgHex({
        type: sourceData.nodeType,
        category: sourceReg?.category ?? null,
      })
    : undefined;
  const sourceOutputLabels = (sourceData?.nodeConfig?.__output_labels ??
    {}) as Record<string, string>;
  const isSourceInputNode =
    sourceData?.nodeType === "InputNode" ||
    sourceData?.nodeType === "InputNodeV2";
  const sourceInputName = isSourceInputNode
    ? (sourceData?.nodeConfig?.label as string | undefined)
    : undefined;
  // Dynamic-config outputs (the backend's get_config projection) carry the
  // authoritative per-output types for nodes whose outputs are config-derived
  // (e.g. Extract from Structured) — the static registry has none for those.
  const overrideOutputs = dynamicNodeConfigs?.get(sourceNodeId)?.outputs as
    | RegistryOutput[]
    | undefined;
  const sourceRegOutput = (
    overrideOutputs ?? (sourceReg?.outputs as RegistryOutput[] | undefined)
  )?.find((o) => o.name === outputName);

  // Declared path variables (`<path>.<name>`) aren't registry outputs — label
  // them "<path label> · <name>" and read as lists (channels are aligned
  // lists). Element type comes from the row's ref one level deep, best-effort.
  if (!sourceRegOutput) {
    const nodeConfig = sourceData?.nodeConfig as
      | Record<string, unknown>
      | undefined;
    const split = splitPathOutputKey(outputName);
    const info = split
      ? pathKeysFor(sourceData?.nodeType, sourceReg, nodeConfig).find(
          (p) => p.key === split.pathKey
        )
      : undefined;
    const row = split
      ? declaredPathVar(nodeConfig, split.pathKey, split.name)
      : undefined;
    if (split && info && row) {
      let rowDataType = "str";
      const whole = row.value.match(WHOLE_VAR_REF_PATTERN);
      const rowRef = whole
        ? parseVarRefId(normalizeVariableRefId(whole[1]))
        : null;
      if (rowRef) {
        const refReg = nodeRegistry.find(
          (r) => r.type === nodeDataMap.get(rowRef.sourceNodeId)?.nodeType
        );
        const refOutputs = (dynamicNodeConfigs?.get(rowRef.sourceNodeId)
          ?.outputs ?? refReg?.outputs) as RegistryOutput[] | undefined;
        const refSpec = refOutputs?.find((o) => o.name === rowRef.outputName);
        rowDataType =
          refSpec?.definition?.data_type ?? refSpec?.data_type ?? "str";
      }
      return {
        sourceNodeId,
        sourceName,
        sourceOutput: formatPathVarLabel(info.label, split.name),
        sourceImage,
        sourceIconName,
        sourceColor,
        isSecret: false,
        sourceIsList: true,
        sourceDataType: rowDataType,
      };
    }
  }

  const originalLabel = sourceRegOutput?.label || outputName;
  const customOutputLabel =
    sourceOutputLabels[outputName] || sourceInputName || originalLabel;
  const isVariableTyped = sourceRegOutput
    ? isVariableTypedConnector(sourceRegOutput)
    : false;
  const nodeConfigType = sourceData?.nodeConfig?.type as string | undefined;
  const parsedConfigType = parseFlowInputType(nodeConfigType);
  const baseDataType =
    sourceRegOutput?.definition?.data_type ?? sourceRegOutput?.data_type;
  const sourceDataType =
    (isVariableTyped || isSourceInputNode) && nodeConfigType
      ? parsedConfigType.dataType
      : baseDataType;
  const nodeConfigIsList =
    !!sourceData?.nodeConfig?.is_list || parsedConfigType.isList;
  const baseIsList =
    sourceRegOutput?.definition?.is_list ?? sourceRegOutput?.is_list ?? false;
  // A source node in iteration mode emits a list (per-item outputs are
  // consolidated), so any of its outputs reads as a list downstream.
  const sourceIsList =
    !!sourceData?.iterate ||
    (isVariableTyped || isSourceInputNode ? nodeConfigIsList : baseIsList);
  return {
    sourceNodeId,
    sourceName,
    sourceOutput: customOutputLabel,
    sourceImage,
    sourceIconName,
    sourceColor,
    isSecret: false,
    sourceIsList,
    sourceDataType,
  };
}

/**
 * Resolve a mention token id (from `((id))` or `:var[id]`) to chip metadata.
 * Accepts both forms used across the editor:
 *   - V2 raw ref:   "<uuid>.<output>"
 *   - Slot key:     "<key>" or "<connector>__<key>", resolved through `bindings`
 * Returns null when the id can't be resolved to a known source.
 */
export function resolveMentionChip(
  mentionId: string,
  bindings: Record<string, string>,
  nodeDataMap: Map<string, NodeV2Data>,
  nodeRegistry: NodeDefinition[],
  dynamicNodeConfigs?: Map<string, DynamicNodeConfig>
): MentionSource | null {
  const id = normalizeVariableRefId(mentionId);
  const dotIdx = id.indexOf(".");
  if (dotIdx > 0) {
    const sourceNodeId = id.slice(0, dotIdx);
    const outputName = id.slice(dotIdx + 1);
    if (nodeDataMap.has(sourceNodeId)) {
      return resolveBindingTarget(
        sourceNodeId,
        outputName,
        nodeDataMap,
        nodeRegistry,
        undefined,
        dynamicNodeConfigs
      );
    }
  }
  for (const [bindingKey, bindingValue] of Object.entries(bindings)) {
    if (bindingKey === id || bindingKey.endsWith(`__${id}`)) {
      const dot = bindingValue.indexOf(".");
      if (dot > 0) {
        const sourceNodeId = bindingValue.slice(0, dot);
        const outputName = bindingValue.slice(dot + 1);
        return resolveBindingTarget(
          sourceNodeId,
          outputName,
          nodeDataMap,
          nodeRegistry,
          undefined,
          dynamicNodeConfigs
        );
      }
    }
  }
  return null;
}
